import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PublicFooter from './PublicFooter';
import AppLogo from './AppLogo';
import HeaderAccountActions from './HeaderAccountActions';
import { plansEnabled } from '../config/featureFlags';

const HEADLINE_WORDS = ['Get', 'hired', 'with', 'a', 'clear', 'EMS', 'readiness', 'profile.'];

/**
 * Sign-up links (2026-10-15, tester fixes E3). The EMS professional button goes straight to the EMT
 * sign-up form (it used to stop first at /signup to pick a role the visitor had just picked); the
 * general "Sign up free" bar on phones still opens /signup, where all three choices and Google are.
 */
const EMT_SIGNUP = '/register/step1?role=emt';
const EMPLOYER_SIGNUP = '/employer-registration';
const INSTITUTE_SIGNUP = '/institute-registration';

/**
 * Real product pictures (tester fixes E2): production demo accounts with made-up people, every write
 * blocked, taken by scripts/landing/landing-shots-1015.mjs. Width and height are the files' own, so
 * the page does not jump while they load.
 */
type Shot = { src: string; w: number; h: number; alt: string; caption: string };
const EMT_SHOTS: Shot[] = [
  { src: '/landing/emt-match.webp', w: 744, h: 388, alt: "A job card showing an 81% match and You're good to go", caption: 'Every job shows your match, and a thumbs up when you meet everything it requires.' },
  { src: '/landing/emt-why.webp', w: 684, h: 1600, alt: 'The Why? panel: how a job score adds up, part by part', caption: 'Tap Why? to see how the number adds up and what would raise it.' },
  { src: '/landing/emt-credentials.webp', w: 700, h: 440, alt: 'An NREMT-B card marked Documents Reviewed, with its expiration date', caption: 'Upload a card once. When it passes our automated checks, employers see it marked Documents Reviewed.' },
  { src: '/landing/emt-resume.webp', w: 800, h: 804, alt: 'A resume Arevina built from an EMT profile', caption: 'No resume handy? Arevina builds one from your profile, and employers get it only after you confirm it.' },
];
const EMPLOYER_SHOTS: Shot[] = [
  { src: '/landing/emp-top-matches.webp', w: 2448, h: 1334, alt: 'Top Matches: three EMTs with their readiness score and what they are missing', caption: 'Top Matches: EMTs with their readiness score, reviewed documents and what they are missing from your Hiring Checklist.' },
  { src: '/landing/emp-breakdown.webp', w: 772, h: 1370, alt: "One candidate's card with Why this score open", caption: "Open Why this score to see what each part of a candidate's number is built from." },
  { src: '/landing/emp-interview.webp', w: 1816, h: 1438, alt: 'The interview request: let them pick, or suggest a time', caption: 'Send an interview request. The candidate picks from your open times and you confirm one.' },
  { src: '/landing/emp-insights.webp', w: 2472, h: 944, alt: 'Insights: open jobs, applicants, interviews, hires and hires by level', caption: 'Insights counts your applicants, interviews and offers, and who you hired, by level.' },
];
const INSTITUTE_SHOTS: Shot[] = [
  { src: '/landing/inst-renewals.webp', w: 2336, h: 1276, alt: 'Renewal leads: names, certification and when it expires', caption: 'Renewal leads: EMTs near you whose certification is coming due, only those who chose to hear from institutes.' },
  { src: '/landing/inst-roster.webp', w: 2336, h: 940, alt: 'A class: sharing progress, profile complete, placed', caption: 'Classes: see who finished their profile, which credentials passed review and who got placed.' },
];

/** Playwright prerender + OAuth crawl: skip entrance animation, stay at final visible state. */
function isPrerenderCapture(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.__PRERENDER__ === true) return true;
  try {
    return new URLSearchParams(window.location.search).get('prerender') === '1';
  } catch {
    return false;
  }
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function ShotFigure({ shot, priority = false }: { shot: Shot; priority?: boolean }) {
  return (
    <figure className="min-w-0" data-testid="landing-shot">
      <div className="overflow-hidden rounded-2xl border border-[var(--border-default)] bg-[#0f1416] shadow-md">
        <img
          src={shot.src}
          width={shot.w}
          height={shot.h}
          alt={shot.alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          className="block h-auto max-h-[36rem] w-full object-cover object-top"
        />
      </div>
      <figcaption className="mt-3 text-sm leading-6 text-[var(--text-secondary)]">{shot.caption}</figcaption>
    </figure>
  );
}

function SectionSignup({ to, label, note, testId }: { to: string; label: string; note: string; testId: string }) {
  return (
    <div className="mt-10 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4">
      <Link
        to={to}
        className="inline-flex min-h-[2.75rem] items-center rounded-xl bg-[var(--rr-red)] px-6 py-3 text-base font-semibold text-white transition hover:bg-[var(--rr-red-hover)]"
        data-testid={testId}
      >
        {label}
      </Link>
      <span className="text-sm text-[var(--text-muted)]">{note}</span>
    </div>
  );
}

export default function MainLandingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const accountDeleted = searchParams.get('accountDeleted') === '1';
  // Start visible so prerendered HTML / hydration match crawler-visible content.
  const [headlineVisible, setHeadlineVisible] = useState(true);
  const [subtextVisible, setSubtextVisible] = useState(true);
  const [createHover, setCreateHover] = useState(false);
  const [featureHover, setFeatureHover] = useState<number | null>(null);
  // Phone: a "Sign up free" bar appears once the hero's own buttons have scrolled away.
  const heroCtaRef = useRef<HTMLDivElement>(null);
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    // Explicit prerender flag: never hide content or depend on animation timing.
    if (isPrerenderCapture() || prefersReducedMotion()) return;

    setHeadlineVisible(false);
    setSubtextVisible(false);

    const headlineTimer = window.setTimeout(() => setHeadlineVisible(true), 100);
    const subtextTimer = window.setTimeout(() => setSubtextVisible(true), 700);
    return () => {
      window.clearTimeout(headlineTimer);
      window.clearTimeout(subtextTimer);
    };
  }, []);

  useEffect(() => {
    const el = heroCtaRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setShowStickyCta(!entry.isIntersecting && entry.boundingClientRect.top < 0), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div className="rr-landing min-h-screen flex flex-col">
      {accountDeleted ? (
        <div
          className="border-b border-green-200 bg-green-50 px-4 py-3 text-center text-sm text-green-900"
          role="status"
        >
          Your account has been permanently deleted.
          <button
            type="button"
            className="ml-3 underline"
            onClick={() => {
              searchParams.delete('accountDeleted');
              setSearchParams(searchParams, { replace: true });
            }}
          >
            Dismiss
          </button>
        </div>
      ) : null}
      <header className="rr-nav sticky top-0 z-50 overflow-visible">
        <div className="rr-nav-inner mx-auto max-w-7xl w-full min-w-0 justify-between gap-2">
          <div className="flex min-w-0 flex-shrink items-center">
            {/* Larger lockup from sm+; default size on narrow so header CTAs fit */}
            <span className="sm:hidden">
              <AppLogo clickable markSize={30} />
            </span>
            <span className="hidden sm:inline-flex">
              <AppLogo clickable markSize={40} />
            </span>
          </div>

          <nav className="hidden items-center gap-1 text-sm md:flex lg:gap-2">
            <a href="#for-emts" className="rr-nav-link whitespace-nowrap">For EMS Professionals</a>
            <a href="#for-agencies" className="rr-nav-link whitespace-nowrap">For agencies</a>
            <a href="#for-institutes" className="rr-nav-link whitespace-nowrap">For institutes</a>
            <a href="#pricing" className="rr-nav-link whitespace-nowrap">Pricing</a>
            {/* The contact form (/contact redirects there too). */}
            <Link to="/support" className="rr-nav-link whitespace-nowrap" data-testid="landing-nav-contact">Contact us</Link>
          </nav>

          {/* Signed out (and the prerendered markup): Sign in / Create account, exactly as before. */}
          <HeaderAccountActions variant="brand" />
        </div>
      </header>

      <main>
        <section className="rr-landing-hero rr-landing-hero-dark relative overflow-hidden">
          <div
            className="pointer-events-none absolute -right-[5%] -top-[5%] z-0 h-[500px] w-[500px]"
            style={{
              background: 'radial-gradient(circle, var(--rr-red-light) 0%, transparent 70%)',
            }}
          />
          <div className="rr-landing-hero-accent" />
          <div className="relative z-[1] mx-auto grid max-w-7xl gap-10 px-4 py-10 sm:gap-14 sm:px-6 sm:py-20 lg:grid-cols-2 lg:px-8 lg:py-24">
            <div className="flex min-w-0 flex-col justify-center">
              <div className="rr-landing-badge mb-4 inline-flex w-fit items-center rounded-full px-4 py-2 text-sm font-medium backdrop-blur-sm sm:mb-6">
                Built for EMS hiring, not generic job boards
              </div>
              <h1
                className="max-w-2xl font-bold text-[var(--text-primary)]"
                aria-label="Get hired with a clear EMS readiness profile."
                style={{
                  fontFamily: 'var(--font-brand)',
                  fontSize: 'clamp(2.2rem, 4vw, 3.5rem)',
                  letterSpacing: '-0.03em',
                  lineHeight: 1.1,
                }}
              >
                {HEADLINE_WORDS.map((word, i) => (
                  <span
                    key={i}
                    className="inline-block"
                    style={{
                      opacity: headlineVisible ? 1 : 0,
                      transform: headlineVisible ? 'translateY(0)' : 'translateY(16px)',
                      transition: `opacity 0.4s ease ${i * 0.08}s, transform 0.4s ease ${i * 0.08}s`,
                      marginRight: '0.25em',
                    }}
                  >
                    {/* The space is real text, so search engines, link previews and text readers get
                        "Get hired with ..." (2026-10-19). A trailing space inside an inline-block takes no
                        width, so the gap on screen is still the margin. */}
                    {i < HEADLINE_WORDS.length - 1 ? `${word} ` : word}
                  </span>
                ))}
              </h1>
              <p
                className="mt-4 max-w-xl text-lg leading-relaxed text-[var(--text-secondary)] sm:mt-6"
                style={{
                  opacity: subtextVisible ? 1 : 0,
                  transition: 'opacity 0.5s ease',
                }}
              >
                Arevina is an EMS recruiting platform that connects EMS professionals with hiring agencies. Your free profile
                shows your credentials (and which ones are Documents Reviewed), your experience, and how well you match each job.
              </p>
              <p
                className="mt-3 max-w-xl text-base leading-relaxed text-[var(--text-secondary)]"
                style={{
                  opacity: subtextVisible ? 1 : 0,
                  transition: 'opacity 0.5s ease 0.1s',
                }}
              >
                Agencies can use Arevina to find EMS professionals nearby and see which of their credentials are Documents Reviewed.
              </p>
              <div ref={heroCtaRef} className="mt-6 flex flex-col gap-4 sm:mt-8 sm:flex-row sm:flex-wrap sm:items-start sm:gap-6">
                <div className="flex flex-col gap-1.5">
                  <Link
                    to={EMT_SIGNUP}
                    className="inline-flex min-h-[2.75rem] w-fit items-center gap-2 rounded-xl px-7 py-4 text-base font-semibold text-white"
                    style={{
                      backgroundColor: createHover ? 'var(--rr-red-hover)' : 'var(--rr-red)',
                      boxShadow: createHover ? '0 2px 8px rgba(0, 0, 0, 0.12)' : 'none',
                      transition: 'background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
                      transform: createHover ? 'translateY(-1px)' : 'translateY(0)',
                    }}
                    onMouseEnter={() => setCreateHover(true)}
                    onMouseLeave={() => setCreateHover(false)}
                    data-testid="landing-hero-emt-signup"
                  >
                    Create a free profile
                  </Link>
                  <span className="text-sm text-[var(--text-muted)]">For EMS professionals. Takes a few minutes.</span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Link
                    to={EMPLOYER_SIGNUP}
                    className="rr-landing-signin-btn inline-flex min-h-[2.75rem] w-fit items-center gap-2 rounded-xl border border-[var(--border-default)] px-7 py-4 text-base font-semibold text-[var(--text-primary)]"
                    data-testid="landing-hero-employer-signup"
                  >
                    Hire EMS professionals
                  </Link>
                  <span className="text-sm text-[var(--text-muted)]">Free for hiring agencies during early access.</span>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                <Link to="/signin" className="font-medium text-[var(--text-muted)] transition hover:text-[var(--text-secondary)]">
                  Already have an account? Sign in
                </Link>
                <Link to={INSTITUTE_SIGNUP} className="font-medium text-[var(--text-muted)] transition hover:text-[var(--text-secondary)]" data-testid="landing-hero-institute-signup">
                  Training institute? List your courses
                </Link>
                {/* Phone has no top navigation: the contact link sits here, near the top. */}
                <Link to="/support" className="font-medium text-[var(--text-muted)] transition hover:text-[var(--text-secondary)] md:hidden" data-testid="landing-hero-contact">
                  Contact us
                </Link>
              </div>
              <div className="mt-8 grid max-w-xl grid-cols-1 gap-4 sm:mt-10 sm:grid-cols-3">
                {[
                  { title: '0 to 100', subtitle: 'Readiness Score', body: 'Employers see one score built from your credentials, reviewed documents, experience, skills, and profile.' },
                  { title: 'Reviewed', subtitle: 'Credential documents', body: 'Documents Reviewed: credential documents that pass our automated checks (name, expiration date, and date of birth when the card shows one). Past jobs can also be confirmed by a former manager.' },
                  { title: 'Direct', subtitle: 'Agency discovery', body: 'EMS professionals get discovered and agencies connect without the resume shuffle.' },
                ].map((item, idx) => (
                  <div
                    key={item.title}
                    className="rr-landing-stat-card min-w-0 cursor-default"
                    style={{
                      transform: featureHover === idx ? 'translateY(-4px)' : 'translateY(0)',
                      boxShadow: featureHover === idx ? 'var(--shadow-md)' : 'var(--shadow-sm)',
                    }}
                    onMouseEnter={() => setFeatureHover(idx)}
                    onMouseLeave={() => setFeatureHover(null)}
                  >
                    <div className="text-xl font-semibold text-[var(--text-primary)] sm:text-2xl">{item.title}</div>
                    <div className="mt-1 text-sm text-[var(--text-muted)]">{item.subtitle}</div>
                    <p className="mt-2 break-words text-sm text-[var(--text-secondary)]">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* The payoff first (E2/E4): what an EMS professional sees on a real job, before any sign-up. */}
            <div className="relative flex items-center justify-center">
              <div className="w-full max-w-md" data-testid="landing-hero-shot">
                <ShotFigure shot={EMT_SHOTS[0]} priority />
                <p className="mt-1 text-xs text-[var(--text-muted)]">A job on Arevina, from a demo account.</p>
              </div>
            </div>
          </div>
        </section>

        <section id="for-emts" className="rr-landing-section-muted scroll-mt-20 py-12 sm:py-20" data-testid="landing-for-emts">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--text-brand)]">For EMS Professionals</div>
              <h2 className="mt-4 text-2xl font-semibold tracking-tight text-[var(--text-primary)] sm:text-3xl md:text-4xl">
                See where you stand on every job before you apply.
              </h2>
              <p className="mt-5 text-lg leading-8 text-[var(--text-secondary)]">
                Each job shows your match score, the same number the employer sees, and what would raise it. Documents
                Reviewed credentials, manager-confirmed past jobs, and a readiness score employers can read at a glance help
                strong candidates stand out.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {EMT_SHOTS.map((s) => (
                <ShotFigure key={s.src} shot={s} />
              ))}
            </div>
            <SectionSignup to={EMT_SIGNUP} label="Create my free profile" note="Takes a few minutes. Your legal name as it appears on your credentials." testId="landing-emt-section-signup" />
          </div>
        </section>

        <section id="for-agencies" className="scroll-mt-20 py-12 sm:py-20" data-testid="landing-for-agencies">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--text-brand)]">For agencies</div>
              <h2 className="mt-4 text-2xl font-semibold tracking-tight text-[var(--text-primary)] sm:text-3xl md:text-4xl">
                Hire with a clearer picture of each candidate.
              </h2>
              <p className="mt-5 text-lg leading-8 text-[var(--text-secondary)]">
                Search EMS professionals, review readiness scores and full profiles at no extra cost, and move from interest to
                interview without leaving Arevina.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2">
              {EMPLOYER_SHOTS.map((s) => (
                <ShotFigure key={s.src} shot={s} />
              ))}
            </div>
            <SectionSignup to={EMPLOYER_SIGNUP} label="Start hiring free" note="Free for hiring agencies during early access." testId="landing-employer-section-signup" />
          </div>
        </section>

        <section id="for-institutes" className="rr-landing-section-muted scroll-mt-20 py-12 sm:py-20" data-testid="landing-for-institutes">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--text-brand)]">For training institutes</div>
              <h2 className="mt-4 text-2xl font-semibold tracking-tight text-[var(--text-primary)] sm:text-3xl md:text-4xl">
                Reach EMTs who need the courses you teach.
              </h2>
              <p className="mt-5 text-lg leading-8 text-[var(--text-secondary)]">
                Arevina reviews every institute first. Once yours is reviewed, EMTs on Arevina near you who are missing a
                certification you teach can find it on Find training, and renewal leads unlock.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2">
              {INSTITUTE_SHOTS.map((s) => (
                <ShotFigure key={s.src} shot={s} />
              ))}
            </div>
            <SectionSignup to={INSTITUTE_SIGNUP} label="List your institute" note="Arevina reviews each institute before EMTs see it." testId="landing-institute-section-signup" />
          </div>
        </section>

        <section id="how-it-works" className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-20 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--text-brand)]">How it works</div>
            <h2 className="mt-4 text-2xl font-semibold tracking-tight text-[var(--text-primary)] sm:text-3xl md:text-4xl">
              A clearer path from documented readiness to hiring.
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:mt-12 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: 'EMS professionals build a professional profile',
                body: 'Credentials, documents, skills, and experience come together in one place with a clear readiness breakdown.',
              },
              {
                title: 'Arevina calculates readiness',
                body: 'Arevina turns credentials, reviewed documents, experience, and skills into a 0 to 100 score that employers can read quickly.',
              },
              {
                title: 'Agencies search and connect',
                body: 'Employers browse searchable profiles with readiness scores, review full candidate details, and message promising candidates directly.',
              },
            ].map((item) => (
              <div key={item.title} className="rr-landing-section-card">
                <div className="text-xl font-semibold text-[var(--text-primary)]">{item.title}</div>
                <p className="mt-4 text-base leading-8 text-[var(--text-secondary)]">{item.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs text-[var(--text-muted)]" data-testid="landing-shots-note">
            Pictures on this page are real Arevina screens from demo accounts. The people in them are made up.
          </p>
        </section>

        <section id="pricing" className="scroll-mt-20 bg-slate-950 py-12 text-white sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {plansEnabled() ? (
              <>
                <div className="max-w-3xl">
                  <div className="text-sm font-semibold uppercase tracking-[0.18em] text-rose-300">Pricing</div>
                  <h2 className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl md:text-4xl">
                    Free plan. Full profile access included.
                  </h2>
                </div>
                {/* Only the Free plan is listed: no paid plan exists and there is no billing code.
                    Do not add paid plans or features here until they are built and enforced. */}
                <div className="mt-8 grid grid-cols-1 gap-4 sm:mt-12 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
                  <div className="rounded-[28px] border border-white/10 bg-[var(--surface-card)]/5 p-6 backdrop-blur">
                    <div className="text-sm font-medium text-slate-300">Free</div>
                    <p className="mt-4 text-sm leading-7 text-slate-300">
                      Search EMS professionals, view full profiles, post jobs, manage applications, and message candidates.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="max-w-3xl" data-testid="landing-pricing">
                <div className="text-sm font-semibold uppercase tracking-[0.18em] text-rose-300">Pricing</div>
                <h2 className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl md:text-4xl">
                  Free for hiring agencies during early access.
                </h2>
                <p className="mt-6 text-lg leading-8 text-slate-300">Included:</p>
                <ul className="mt-3 space-y-2 text-base leading-7 text-slate-300" data-testid="landing-pricing-included">
                  <li>Search EMS professionals and view full profiles</li>
                  <li>Post jobs and manage applicants</li>
                  <li>Message candidates and send interview requests</li>
                  <li>No plan or billing required</li>
                </ul>
                <Link
                  to={EMPLOYER_SIGNUP}
                  className="mt-8 inline-flex min-h-[2.75rem] items-center rounded-xl bg-white px-6 py-3 text-base font-semibold text-slate-950 transition hover:bg-slate-100"
                  data-testid="landing-pricing-signup"
                >
                  Start hiring free
                </Link>
              </div>
            )}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-20 lg:px-8">
          <div className="flex flex-col gap-6 rounded-2xl border border-[var(--border-subtle)] bg-gradient-to-r from-rose-600 to-rose-700 px-5 py-8 text-white shadow-xl sm:rounded-[32px] sm:px-8 sm:py-10 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <div className="text-2xl font-semibold tracking-tight sm:text-3xl">A hiring platform built around documented readiness.</div>
              <p className="mt-3 max-w-2xl break-words text-sm leading-7 text-white/90 sm:text-base sm:leading-8">
                Arevina helps EMS professionals present stronger professional signals and gives agencies a clearer path to candidates who fit.
              </p>
            </div>
            <div className="flex flex-shrink-0 flex-wrap gap-3 sm:gap-4">
              <Link to={EMT_SIGNUP} className="flex min-h-[2.75rem] items-center rounded-xl bg-[var(--surface-card)] px-5 py-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-50 sm:rounded-2xl">
                Create a free profile
              </Link>
              <Link to={EMPLOYER_SIGNUP} className="flex min-h-[2.75rem] items-center rounded-xl border border-white/50 px-5 py-3 text-sm font-semibold text-white transition hover:bg-[var(--surface-card)]/10 sm:rounded-2xl">
                Hire EMS professionals
              </Link>
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />
      {/* Phone: once the hero's buttons have scrolled away, "Sign up free" stays at the bottom (E3). */}
      {showStickyCta && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.12)] md:hidden"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          data-testid="landing-sticky-signup"
        >
          <div className="flex items-center gap-3">
            <Link to="/signup" className="flex min-h-[2.75rem] flex-1 items-center justify-center rounded-xl bg-[var(--rr-red)] text-base font-semibold text-white">
              Sign up free
            </Link>
            <Link to="/signin" className="flex min-h-[2.75rem] items-center px-2 text-sm font-medium text-[var(--text-secondary)]">
              Sign in
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
